import React, { useMemo } from "react";
import { PointsMaterial, AdditiveBlending } from "three";
import { PointCloudProps } from "@/types/laser-engraving";

/**
 * 点云组件
 *
 * 专门负责渲染激光内雕点云，具有以下特性：
 * - 基于物理光学模型的激光内雕点云
 * - 支持物理正确的尺寸衰减
 * - 多种混合模式选择
 * - 优化的渲染性能
 * - 每点独立颜色支持
 */
export const PointCloud = React.memo<PointCloudProps>(
  ({ geometry, config, visible = true }) => {
    // 🎯 点云材质 - 启用物理正确的尺寸衰减
    const pointMaterial = useMemo(() => {
      return new PointsMaterial({
        size: config.pointSize * config.sizeMultiplier, // 可调节的世界尺寸
        vertexColors: true, // 启用每点独立颜色
        sizeAttenuation: true, // 启用物理正确的尺寸衰减
        blending: AdditiveBlending, // 标准混合模式
        depthWrite: true, // 粒子深度策略
        transparent: false,
        opacity: config.opacity, // 可调节透明度
      });
    }, [config.pointSize, config.sizeMultiplier, config.opacity]);

    // 如果没有几何体或不可见，不渲染
    if (!geometry || !visible) {
      return null;
    }

    return (
      <points
        geometry={geometry}
        material={pointMaterial}
        castShadow={false}
        receiveShadow={false}
        // 🎯 确保点云完全不参与阴影系统
        visible={visible}
        frustumCulled={false}
      />
    );
  },
);

PointCloud.displayName = "PointCloud";

export default PointCloud;
